import type { FC } from "react"
import { useId, useState, useEffect } from "react"

import { Wrapper } from "../ui"

type SimpleNumberInputProps = {
  value: number | null
  setValue: (val: number | null) => void
  label?: string
  placeholder?: string
  decimalPlaces?: number
  disabled?: boolean
  isError?: boolean
  defaultValue?: string
  name?: string
  testId?: string
  min?: number
  max?: number
  onBlur?: (parsedValue: number | null) => void
  style?: React.CSSProperties
  loading?: boolean
}

const SimpleNumberInput: FC<SimpleNumberInputProps> = ({
  value,
  setValue,
  label,
  placeholder = `-`,
  decimalPlaces = 2,
  disabled = false,
  isError = false,
  defaultValue,
  name,
  testId,
  min,
  max,
  onBlur,
  style,
}) => {
  const id = useId()
  const [raw, setRaw] = useState<string>(value?.toFixed(decimalPlaces) ?? ``)

  useEffect(() => {
    // Keep internal state in sync if external value changes
    setRaw(value?.toFixed(decimalPlaces) ?? ``)
  }, [value, decimalPlaces])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRaw(e.target.value)
  }

  const handleBlur = () => {
    const parsed = parseFloat(raw)

    if (!isNaN(parsed)) {
      let clamped = parsed
      if (min !== undefined) clamped = Math.max(clamped, min)
      if (max !== undefined) clamped = Math.min(clamped, max)
      setValue(clamped)
      setRaw(clamped.toFixed(decimalPlaces))
      onBlur?.(clamped)
    } else {
      setValue(null)
      setRaw(``)
      onBlur?.(null)
    }
  }

  return (
    <Wrapper data-is-error={isError.toString()}>
      {label && <label htmlFor={id}>{label}</label>}
      <input
        id={id}
        name={name ?? id}
        type="text"
        value={raw}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`number-input-${testId ?? id}`}
        style={style}
      />
    </Wrapper>
  )
}

export const SimpleNumberInputInline: FC<SimpleNumberInputProps> = ({
  value,
  setValue,
  label,
  placeholder = `-`,
  decimalPlaces = 2,
  disabled = false,
  isError = false,
  defaultValue,
  name,
  testId,
  min,
  max,
  onBlur,
  style,
  loading,
}) => {
  const id = useId()
  const [raw, setRaw] = useState<string>(value?.toFixed(decimalPlaces) ?? ``)

  useEffect(() => {
    // Keep internal state in sync if external value changes
    setRaw(value?.toFixed(decimalPlaces) ?? ``)
  }, [value, decimalPlaces])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRaw(e.target.value)
  }

  const handleBlur = () => {
    const parsed = parseFloat(raw)

    if (!isNaN(parsed)) {
      let clamped = parsed
      if (min !== undefined) clamped = Math.max(clamped, min)
      if (max !== undefined) clamped = Math.min(clamped, max)
      setValue(clamped)
      setRaw(clamped.toFixed(decimalPlaces))
      onBlur?.(clamped)
    } else {
      setValue(null)
      setRaw(``)
      onBlur?.(null)
    }
  }

  return (
    <div className="relative ml-auto flex w-fit items-center bg-gray-50">
      {/* <div className="relative mx-auto mt-2 flex w-1/2 items-center bg-gray-50"> */}
      {loading ? (
        <span className="">
          <svg
            className="h-4 w-4 animate-spin text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </span>
      ) : (
        <span className="mr-1">$</span>
      )}

      <input
        id={id}
        name={name ?? id}
        type="text"
        value={raw}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`number-input-${testId ?? id}`}
        className="peer w-24 bg-gray-50 px-3 py-2 text-end text-gray-900 placeholder:text-gray-500 focus:outline-0 sm:text-sm/6"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 border-t border-solid border-gray-300 peer-focus:border-t-2 peer-focus:border-sky-500"
      />
    </div>
  )
}

export default SimpleNumberInput
